import React from 'react';
import { useNavigate } from 'react-router-dom';

import { Modal } from 'src/components/Modal/Modal';
import {
  AdminWallet,
  formatWalletBalance,
  formatWalletOwnerName,
} from 'src/api/adminWallets';
import { convertDate, formatFieldString, shortenString } from 'src/components/Table/helpers';
import { ROUTE_USERS } from 'src/utils/routes';

import {
  Breadcrumb,
  Card,
  CardTitle,
  DetailGrid,
  DetailItem,
  DetailLabel,
  DetailValue,
  HeadlineRow,
  MetaLine,
  StatusBadge,
  Title,
  TitleGroup,
  UserLink,
} from 'src/pages/Transactions/styled';
import CopyableValue from 'src/pages/Transactions/CopyableValue';

type WalletDetailModalProps = {
  wallet: AdminWallet;
  onClose: () => void;
};

const SectionSpacer = () => <div style={{ height: '1rem' }} />;


const WalletDetailModal: React.FC<WalletDetailModalProps> = ({ wallet, onClose }) => {
  const navigate = useNavigate();

  const owner = wallet.owner;
  const ownerId = owner?.id;
  const formattedOwnerName = formatWalletOwnerName(owner);
  const ownerName =
    formattedOwnerName !== '—' ? formattedOwnerName : owner?.email || '—';
  const ownerEmail = owner?.email;
  const ownerType = wallet.owner_type ? String(wallet.owner_type) : '';
  const canOpenUser = Boolean(ownerId) && ownerType.toLowerCase() === 'user';

  const currency = wallet.currency_code || '';
  const blockchain = wallet.currency_name || '';
  const balanceLabel = formatWalletBalance(wallet.balance, wallet.currency_code);
  const availableLabel = formatWalletBalance(wallet.available_balance, wallet.currency_code);
  const createdAt = wallet.created_at;
  const updatedAt = wallet.updated_at;

  const headline =
    [currency || null, blockchain || null].filter(Boolean).join(' · ') || 'Wallet';

  const createdMeta = createdAt
    ? `Created ${convertDate(createdAt).date}${
        convertDate(createdAt).time ? ` · ${convertDate(createdAt).time}` : ''
      }`
    : null;

  return (
    <Modal isOpen onClose={onClose}>
      <Breadcrumb>Wallets{wallet.id ? ` / ${shortenString(wallet.id)}` : ''}</Breadcrumb>

      <HeadlineRow>
        <TitleGroup>
          <Title>
            {headline}
            {ownerType ? (
              <StatusBadge $status={ownerType}>{formatFieldString(ownerType)}</StatusBadge>
            ) : null}
          </Title>
          {createdMeta ? <MetaLine>{createdMeta}</MetaLine> : null}
        </TitleGroup>
      </HeadlineRow>

      {(ownerName || ownerId || ownerEmail) && (
        <>
          <Card>
            <CardTitle>User</CardTitle>
            <DetailGrid>
              <DetailItem>
                <DetailLabel>Name</DetailLabel>
                {canOpenUser ? (
                  <DetailValue title={ownerEmail ? `${ownerName} (${ownerEmail})` : ownerName}>
                    <UserLink
                      type="button"
                      onClick={() => {
                        onClose();
                        navigate(`${ROUTE_USERS}/${ownerId}`);
                      }}
                    >
                      {ownerName && ownerName !== '—' ? ownerName : shortenString(String(ownerId))}
                    </UserLink>
                  </DetailValue>
                ) : (
                  <DetailValue>{ownerName || '—'}</DetailValue>
                )}
              </DetailItem>
              <DetailItem>
                <DetailLabel>Email</DetailLabel>
                <DetailValue $muted={!ownerEmail}>{ownerEmail || '—'}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>BxLend ID</DetailLabel>
                <DetailValue $muted={!owner?.bxlend_id}>{owner?.bxlend_id || '—'}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Owner type</DetailLabel>
                <DetailValue $muted={!ownerType}>
                  {ownerType ? formatFieldString(ownerType) : '—'}
                </DetailValue>
              </DetailItem>
            </DetailGrid>
          </Card>
          <SectionSpacer />
        </>
      )}

      <Card>
        <CardTitle>Wallet</CardTitle>
        <DetailGrid>
          {wallet.id ? (
            <DetailItem>
              <DetailLabel>Wallet ID</DetailLabel>
              <CopyableValue value={wallet.id} label="Wallet ID" />
            </DetailItem>
          ) : null}
          <DetailItem>
            <DetailLabel>Currency</DetailLabel>
            <DetailValue $muted={!currency}>{currency || '—'}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Blockchain</DetailLabel>
            <DetailValue $muted={!blockchain}>{blockchain || '—'}</DetailValue>
          </DetailItem>
          {wallet.kind ? (
            <DetailItem>
              <DetailLabel>Kind</DetailLabel>
              <DetailValue>{formatFieldString(String(wallet.kind))}</DetailValue>
            </DetailItem>
          ) : null}
          <DetailItem>
            <DetailLabel>Balance</DetailLabel>
            <DetailValue>{balanceLabel}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Available balance</DetailLabel>
            <DetailValue>{availableLabel}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Address</DetailLabel>
            {wallet.address ? (
              <CopyableValue value={wallet.address} label="Address" />
            ) : (
              <DetailValue $muted>—</DetailValue>
            )}
          </DetailItem>
          {createdAt ? (
            <DetailItem>
              <DetailLabel>Created</DetailLabel>
              <DetailValue>
                {convertDate(createdAt).date}
                {convertDate(createdAt).time ? ` · ${convertDate(createdAt).time}` : ''}
              </DetailValue>
            </DetailItem>
          ) : null}
          {updatedAt ? (
            <DetailItem>
              <DetailLabel>Updated</DetailLabel>
              <DetailValue>
                {convertDate(updatedAt).date}
                {convertDate(updatedAt).time ? ` · ${convertDate(updatedAt).time}` : ''}
              </DetailValue>
            </DetailItem>
          ) : null}
        </DetailGrid>
      </Card>
    </Modal>
  );
};

export default WalletDetailModal;
